// src/pages/Landing.js - Public Landing Page
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import LanguageSwitcher from '../components/common/LanguageSwitcher';
import ThemeSwitcher from '../components/common/ThemeSwitcher';
import FriendlyAnalysis from '../components/common/FriendlyAnalysis';
import DisclaimerFooter from '../components/common/DisclaimerFooter';
import { getUser, clearAuth, isAdmin, isJournalist } from '../utils/auth';
import api from '../utils/api';

const Landing = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = getUser();
  const isAuthenticated = !!user;
  const [stats, setStats] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [userStats, setUserStats] = useState(null);
  
  // Trial Tools State
  const [trialUsed, setTrialUsed] = useState(false);
  const [trialLoading, setTrialLoading] = useState(false);
  const [trialResult, setTrialResult] = useState(null);
  const [activeTool, setActiveTool] = useState('hate-speech'); // 'hate-speech', 'ai-detection', 'fact-check'
  const [trialInput, setTrialInput] = useState('');
  const [trialFile, setTrialFile] = useState(null);
  const [trialError, setTrialError] = useState('');
  const [factCheckPlaceholder, setFactCheckPlaceholder] = useState('e.g. "Water boils at 100 degrees"');
  const hasLoadedStatsRef = useRef(false);
  const hasLoadedArticleRef = useRef(false);

  // Set document direction on mount and language change
  useEffect(() => {
    const lng = localStorage.getItem('language') || 'ar';
    document.documentElement.dir = lng === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lng;
    
    // Check if trial was already used
    const used = localStorage.getItem('shayek_trial_used');
    if (used) setTrialUsed(true);
  }, [i18n.language]);

  const handleTrialSubmit = async (e) => {
    e.preventDefault();
    if (trialUsed && !trialResult) return;
    
    setTrialLoading(true);
    setTrialError('');
    setTrialResult(null);

    try {
      let response;
      if (activeTool === 'hate-speech') {
        response = await api.post('/api/public/moderate/text', { text: trialInput });
        setTrialResult(response.data.result);
      } else if (activeTool === 'fact-check') {
        response = await api.post('/api/public/fact-check', { text: trialInput });
        setTrialResult(response.data.fact_check);
      } else if (activeTool === 'ai-detection') {
        if (!trialFile) {
          setTrialError('Please select a file first.');
          setTrialLoading(false);
          return;
        }
        const formData = new FormData();
        formData.append('file', trialFile);
        response = await api.post('/api/public/moderate/image', formData, {});
        setTrialResult(response.data.result);
      }
      
      setTrialUsed(true);
      localStorage.setItem('shayek_trial_used', 'true');
    } catch (err) {
      console.error('Trial tool error:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Failed to analyze. Please try again later.';
      const errorDetails = err.response?.data?.details;
      setTrialError(errorDetails ? `${errorMessage}: ${errorDetails}` : errorMessage);
    } finally {
      setTrialLoading(false);
    }
  };

  // Load user profile if authenticated
  useEffect(() => {
    if (isAuthenticated && !userProfile) {
      const loadUserProfile = async () => {
        try {
          const response = await api.get('/api/profile/me').catch(() => null);
          if (response && response.data) {
            setUserProfile(response.data.user);
          }
        } catch (error) {
          // Silently fail - profile is optional
          console.log('Could not load user profile');
        }
      };
      loadUserProfile();
    }
  }, [isAuthenticated, userProfile]);

  // Load public statistics (if available)
  useEffect(() => {
    // Prevent duplicate calls (React StrictMode)
    if (hasLoadedStatsRef.current) return;
    hasLoadedStatsRef.current = true;

    const loadStats = async () => {
      try {
        // Try to get public stats (this endpoint may not exist yet)
        const response = await api.get('/api/analytics/public-stats').catch(() => null);
        if (response && response.data) {
          setStats(response.data);
        }
      } catch (error) {
        // Ignore errors - stats are optional
        console.log('Public stats not available');
      }
    };
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load user-specific stats if authenticated (admin uses /moderate/reports; others use /api/reports/reports to avoid 403)
  useEffect(() => {
    if (isAuthenticated && !userStats) {
      const loadUserStats = async () => {
        try {
          const isAdminUser = user?.role === 'admin';
          const endpoint = isAdminUser ? '/moderate/reports?limit=1' : '/api/reports/reports?limit=1';
          const response = await api.get(endpoint).catch(() => null);
          if (response && response.data) {
            const count = response.data.count ?? response.data.reports?.length ?? response.data.total ?? 0;
            setUserStats({ totalReports: count });
          }
        } catch (error) {
          // Silently fail - user stats are optional
        }
      };
      loadUserStats();
    }
  }, [isAuthenticated, userStats, user?.role]);

  const handleLogout = () => {
    clearAuth();
    setShowUserMenu(false);
    navigate('/login');
  };

  const getUserDisplayName = () => {
    if (userProfile?.name) return userProfile.name;
    if (user?.name) return user.name;
    if (user?.email) return user.email.split('@')[0];
    return 'User';
  };

  const getUserInitials = () => {
    const name = getUserDisplayName();
    return name.charAt(0).toUpperCase();
  };

  // Load random article title for fact-check placeholder
  useEffect(() => {
    // Prevent duplicate calls (React StrictMode)
    if (hasLoadedArticleRef.current) return;
    hasLoadedArticleRef.current = true;

    const loadRandomArticleTitle = async () => {
      try {
        const response = await api.get('/api/news/articles/random-title').catch(() => null);
        if (response && response.data && response.data.title) {
          setFactCheckPlaceholder(`e.g. "${response.data.title}"`);
        }
      } catch (error) {
        // Ignore errors - fallback to default placeholder
        console.log('Could not load random article title, using default placeholder');
      }
    };
    loadRandomArticleTitle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const features = [
    {
      icon: '🤖',
      title: t('landing.features.ai.title'),
      description: t('landing.features.ai.description')
    },
    {
      icon: '✅',
      title: t('landing.features.verification.title'),
      description: t('landing.features.verification.description')
    },
    {
      icon: '📊',
      title: t('landing.features.monitoring.title'),
      description: t('landing.features.monitoring.description')
    },
    {
      icon: '🎭',
      title: t('landing.features.aiDetection.title'),
      description: t('landing.features.aiDetection.description')
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-shayek-light-50 to-shayek-light-100 dark:from-shayek-dark dark:to-shayek-dark-50 transition-colors">
      {/* Header */}
      <header className="bg-white dark:bg-shayek-dark shadow-sm border-b border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center">
              <img
                src="/logo-chayyek.png"
                alt={t('common.appName') || 'Chayyek AI'}
                className="h-16 md:h-20 w-auto"
              />
            </Link>
            <div className="flex items-center gap-4">
              <ThemeSwitcher />
              <LanguageSwitcher />
              <a
                href="https://www.chayyek.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-[#751F19] dark:text-[#6C5723] hover:opacity-80 transition-opacity"
              >
                Go to Chayyek | انتقل لشييك
              </a>
              {isAuthenticated ? (
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-shayek-light-50 dark:hover:bg-shayek-dark-50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      {userProfile?.avatar_url || user?.avatar_url ? (
                        <img
                          src={userProfile?.avatar_url || user?.avatar_url}
                          alt={getUserDisplayName()}
                          className="w-10 h-10 rounded-full object-cover border-2 border-shayek-primary/20"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-shayek-primary to-shayek-primary-600 flex items-center justify-center text-white text-sm font-bold border-2 border-shayek-primary/20">
                          {getUserInitials()}
                        </div>
                      )}
                      <span className="hidden sm:block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700">
                        {getUserDisplayName()}
                      </span>
                    </div>
                    <svg className="w-4 h-4 text-shayek-light-500 dark:text-shayek-dark-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {showUserMenu && (
                    <>
                      <div 
                        className="fixed inset-0 z-10" 
                        onClick={() => setShowUserMenu(false)}
                      />
                      <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-shayek-dark-100 rounded-lg shadow-lg border border-shayek-light-300 dark:border-shayek-dark-200 z-20 py-2">
                        <div className="px-4 py-3 border-b border-shayek-light-200 dark:border-shayek-dark-200">
                          <p className="text-sm font-medium text-shayek-light-900 dark:text-shayek-dark-900">{getUserDisplayName()}</p>
                          <p className="text-xs text-shayek-light-500 dark:text-shayek-dark-500 truncate">{user?.email}</p>
                        </div>
                        <Link
                          to="/dashboard"
                          onClick={() => setShowUserMenu(false)}
                          className="block px-4 py-2 text-sm text-shayek-light-700 dark:text-shayek-dark-700 hover:bg-shayek-light-50 dark:hover:bg-shayek-dark-50 transition-colors"
                        >
                          Dashboard
                        </Link>
                        {isAdmin() && (
                          <Link
                            to="/admin"
                            onClick={() => setShowUserMenu(false)}
                            className="block px-4 py-2 text-sm text-shayek-light-700 dark:text-shayek-dark-700 hover:bg-shayek-light-50 dark:hover:bg-shayek-dark-50 transition-colors"
                          >
                            Admin Panel
                          </Link>
                        )}
                        {isJournalist() && (
                          <Link
                            to="/journalist"
                            onClick={() => setShowUserMenu(false)}
                            className="block px-4 py-2 text-sm text-shayek-light-700 dark:text-shayek-dark-700 hover:bg-shayek-light-50 dark:hover:bg-shayek-dark-50 transition-colors"
                          >
                            {t('dashboard.journalistTeam')}
                          </Link>
                        )}
                        <Link
                          to="/profile"
                          onClick={() => setShowUserMenu(false)}
                          className="block px-4 py-2 text-sm text-shayek-light-700 dark:text-shayek-dark-700 hover:bg-shayek-light-50 dark:hover:bg-shayek-dark-50 transition-colors"
                        >
                          Profile Settings
                        </Link>
                        <div className="border-t border-shayek-light-200 dark:border-shayek-dark-200 my-2" />
                        <button
                          onClick={handleLogout}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                          Logout
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="px-4 py-2 text-sm text-shayek-light-700 dark:text-shayek-dark-700 hover:text-shayek-light-900 dark:hover:text-shayek-dark-900 transition-colors"
                  >
                    {t('landing.login')}
                  </Link>
                  <Link
                    to="/register"
                    className="px-6 py-2 text-sm font-medium text-white bg-shayek-primary dark:bg-shayek-primary-600 rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-light transition-colors shadow-shayek"
                  >
                    {t('landing.register') || 'Get Started'}
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {isAuthenticated ? (
          <div className="mb-16">
            {/* Personalized Welcome Section */}
            <div className="bg-gradient-to-r from-shayek-primary to-shayek-primary-600 dark:from-shayek-primary-600 dark:to-shayek-primary rounded-2xl shadow-shayek-lg p-8 md:p-12 text-white mb-12">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex-shrink-0">
                  {userProfile?.avatar_url || user?.avatar_url ? (
                    <img
                      src={userProfile?.avatar_url || user?.avatar_url}
                      alt={getUserDisplayName()}
                      className="w-24 h-24 rounded-full object-cover border-4 border-white/30 shadow-lg"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white text-3xl font-bold border-4 border-white/30 shadow-lg">
                      {getUserInitials()}
                    </div>
                  )}
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h2 className="text-3xl md:text-4xl font-bold mb-3">
                    {t('landing.welcome.title')}
                  </h2>
                  <p className="text-lg md:text-xl opacity-90 mb-6">
                    {t('landing.welcome.description')}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                    <Link
                      to="/dashboard"
                      className="px-8 py-3 text-lg font-semibold bg-white text-shayek-primary rounded-lg hover:bg-shayek-light-50 transition-colors shadow-lg inline-block text-center"
                    >
                      {t('landing.welcome.dashboardButton')}
                    </Link>
                    <button
                      onClick={() => {
                        const element = document.getElementById('trial-tools');
                        element?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="px-8 py-3 text-lg font-semibold bg-white/10 backdrop-blur-sm text-white rounded-lg hover:bg-white/20 transition-colors border-2 border-white/30"
                    >
                      {t('landing.welcome.startButton')}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions for Logged-in Users */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
              <Link
                to="/dashboard"
                className="bg-white dark:bg-shayek-dark-100 rounded-xl shadow-shayek p-6 hover:shadow-shayek-lg transition-all border border-shayek-light-300 dark:border-shayek-dark-200 group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-shayek-primary/10 dark:bg-shayek-primary/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    <svg className="w-6 h-6 text-shayek-primary dark:text-shayek-red-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-1">Dashboard</h3>
                    <p className="text-sm text-shayek-light-600 dark:text-shayek-dark-600">Access your moderation tools and analysis</p>
                  </div>
                </div>
              </Link>

              <Link
                to="/profile"
                className="bg-white dark:bg-shayek-dark-100 rounded-xl shadow-shayek p-6 hover:shadow-shayek-lg transition-all border border-shayek-light-300 dark:border-shayek-dark-200 group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-shayek-primary/10 dark:bg-shayek-primary/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    <svg className="w-6 h-6 text-shayek-primary dark:text-shayek-red-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-1">Profile</h3>
                    <p className="text-sm text-shayek-light-600 dark:text-shayek-dark-600">Manage your account settings</p>
                  </div>
                </div>
              </Link>

              {isAdmin() || isJournalist() ? (
                <Link
                  to={isAdmin() ? "/admin" : "/journalist"}
                  className="bg-white dark:bg-shayek-dark-100 rounded-xl shadow-shayek p-6 hover:shadow-shayek-lg transition-all border border-shayek-light-300 dark:border-shayek-dark-200 group"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-shayek-primary/10 dark:bg-shayek-primary/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                      <svg className="w-6 h-6 text-shayek-primary dark:text-shayek-red-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-1">
                        {isAdmin() ? 'Admin Panel' : t('dashboard.journalistTeam')}
                      </h3>
                      <p className="text-sm text-shayek-light-600 dark:text-shayek-dark-600">
                        {isAdmin() ? 'Manage platform settings' : 'Track investigations'}
                      </p>
                    </div>
                  </div>
                </Link>
              ) : (
                <div className="bg-white dark:bg-shayek-dark-100 rounded-xl shadow-shayek p-6 border border-shayek-light-300 dark:border-shayek-dark-200">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-shayek-primary/10 dark:bg-shayek-primary/20 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-shayek-primary dark:text-shayek-red-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-1">AI Tools</h3>
                      <p className="text-sm text-shayek-light-600 dark:text-shayek-dark-600">Try our powerful analysis tools</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-shayek-light-900 dark:text-shayek-dark-900 mb-4">
              {t('landing.hero.title')}
            </h2>
            <p className="text-xl text-shayek-light-600 dark:text-shayek-dark-600 mb-8 max-w-3xl mx-auto">
              {t('landing.hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => {
                  const element = document.getElementById('trial-tools');
                  element?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="px-8 py-4 text-lg font-semibold text-white bg-shayek-primary dark:bg-shayek-primary-600 rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-light transition-colors shadow-shayek-lg"
              >
                {t('landing.hero.tryTools') || 'Try AI Tools'}
              </button>
              <button
                onClick={() => {
                  const element = document.getElementById('features');
                  element?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="px-8 py-4 text-lg font-semibold text-shayek-light-700 dark:text-shayek-dark-700 bg-white dark:bg-shayek-dark-100 rounded-lg hover:bg-shayek-light-50 dark:hover:bg-shayek-dark-50 transition-colors shadow-lg border border-shayek-light-300 dark:border-shayek-dark-200"
              >
                {t('landing.hero.learnMore')}
              </button>
            </div>
          </div>
        )}

        {/* Statistics Section - visible only to admin/journalist */}
        {stats && (isAdmin() || isJournalist()) && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
            <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 text-center border border-shayek-light-300 dark:border-shayek-dark-200">
              <div className="text-3xl font-bold text-shayek-primary dark:text-shayek-red-light mb-2">
                {stats.totalReports?.toLocaleString() || '0'}
              </div>
              <div className="text-sm text-shayek-light-600 dark:text-shayek-dark-600">{t('landing.stats.totalReports')}</div>
            </div>
            <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 text-center border border-shayek-light-300 dark:border-shayek-dark-200">
              <div className="text-3xl font-bold text-shayek-category-correct dark:text-shayek-category-correct mb-2">
                {stats.verifiedReports?.toLocaleString() || '0'}
              </div>
              <div className="text-sm text-shayek-light-600 dark:text-shayek-dark-600">{t('landing.stats.verifiedReports')}</div>
            </div>
            <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 text-center border border-shayek-light-300 dark:border-shayek-dark-200">
              <div className="text-3xl font-bold text-shayek-category-misleading dark:text-shayek-category-misleading mb-2">
                {stats.activeInvestigations?.toLocaleString() || '0'}
              </div>
              <div className="text-sm text-shayek-light-600 dark:text-shayek-dark-600">{t('landing.stats.activeInvestigations')}</div>
            </div>
            <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 text-center border border-shayek-light-300 dark:border-shayek-dark-200">
              <div className="text-3xl font-bold text-shayek-category-misleading-title dark:text-shayek-category-misleading-title mb-2">
                {stats.newsSources?.toLocaleString() || '0'}
              </div>
              <div className="text-sm text-shayek-light-600 dark:text-shayek-dark-600">{t('landing.stats.newsSources')}</div>
            </div>
          </div>
        )}

        {/* Trial Tools Section */}
        <div id="trial-tools" className="mb-24 scroll-mt-20">
          <div className="max-w-3xl mx-auto bg-white dark:bg-shayek-dark-100 rounded-2xl shadow-shayek-lg overflow-hidden border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
            <div className="p-8 border-b border-shayek-light-300 dark:border-shayek-dark-200 bg-shayek-light-50 dark:bg-shayek-dark-50">
              <h3 className="text-2xl font-bold text-center text-shayek-light-900 dark:text-shayek-dark-900">
                {t('landing.trial.title') || 'Experience Our AI Analysis'}
              </h3>
              <p className="text-center text-shayek-light-600 dark:text-shayek-dark-600 mt-2">
                {t('landing.trial.subtitle') || 'Try our industry-leading moderation tools for free. Limited to one trial per visitor.'}
              </p>
            </div>

            <div className="p-8">
              {trialUsed && !trialResult ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg className="w-10 h-10 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold mb-4">{t('landing.trial.limitTitle') || 'Trial Limit Reached'}</h4>
                  <p className="text-shayek-light-600 dark:text-shayek-dark-600 mb-8 max-w-sm mx-auto">
                    {t('landing.trial.limitMessage') || 'You have already used your free trial. Create an account to get full access to all features.'}
                  </p>
                  <Link
                    to="/register"
                    className="inline-block px-8 py-3 bg-shayek-primary text-white rounded-lg font-bold hover:bg-shayek-primary-600 transition-colors shadow-shayek"
                  >
                    {t('landing.register') || 'Create Free Account'}
                  </Link>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Tool Selector */}
                  {!trialResult && (
                    <div className="flex p-1 bg-shayek-light-100 dark:bg-shayek-dark-200 rounded-xl mb-6">
                      {[
                        { id: 'hate-speech', labelKey: 'landing.trial.tools.hateSpeech' },
                        { id: 'ai-detection', labelKey: 'landing.trial.tools.aiDetection' },
                        { id: 'fact-check', labelKey: 'landing.trial.tools.factCheck' }
                      ].map(tool => (
                        <button
                          key={tool.id}
                          onClick={() => {
                            setActiveTool(tool.id);
                            setTrialError('');
                          }}
                          className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-all ${
                            activeTool === tool.id
                              ? 'bg-white dark:bg-shayek-dark-100 text-shayek-primary dark:text-shayek-red-light shadow-sm'
                              : 'text-shayek-light-500 dark:text-shayek-dark-500 hover:text-shayek-light-700 dark:hover:text-shayek-dark-700'
                          }`}
                        >
                          {t(tool.labelKey)}
                        </button>
                      ))}
                    </div>
                  )}
                  {!trialResult && (
                    <DisclaimerFooter className="mt-4" />
                  )}

                  {trialResult ? (
                    <div className="space-y-6 animate-fade-in">
                      <div className="p-6 bg-shayek-light-50 dark:bg-shayek-dark-50 rounded-xl border border-shayek-light-300 dark:border-shayek-dark-200">
                        <FriendlyAnalysis data={trialResult} type={activeTool === 'fact-check' ? 'fact_check' : 'ai'} />
                      </div>
                      <div className="text-center bg-shayek-primary/5 dark:bg-shayek-primary/10 p-6 rounded-xl border border-shayek-primary/10">
                        <p className="font-medium text-shayek-light-900 dark:text-shayek-dark-900 mb-4">
                          {t('landing.trial.finished') || 'Ready for professional-grade analysis?'}
                        </p>
                        <Link
                          to="/register"
                          className="px-8 py-3 bg-shayek-primary text-white rounded-lg font-bold hover:bg-shayek-primary-600 transition-colors inline-block"
                        >
                          {t('landing.register') || 'Join Shayek Today'}
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleTrialSubmit} className="space-y-4">
                      {activeTool === 'ai-detection' ? (
                        <div className="space-y-4">
                          <label className="block text-sm font-medium mb-2">Upload Image or Video</label>
                          <div className="border-2 border-dashed border-shayek-light-300 dark:border-shayek-dark-200 rounded-xl p-8 text-center hover:border-shayek-primary transition-colors cursor-pointer bg-shayek-light-50 dark:bg-shayek-dark-50 relative">
                            <input
                              type="file"
                              accept="image/*,video/mp4"
                              onChange={(e) => setTrialFile(e.target.files[0])}
                              className="absolute inset-0 opacity-0 cursor-pointer"
                            />
                            {trialFile ? (
                              <div className="text-shayek-primary font-medium">
                                <p>Selected: {trialFile.name}</p>
                                <p className="text-xs text-gray-500">Click to change</p>
                              </div>
                            ) : (
                              <div className="text-shayek-light-500">
                                <svg className="w-12 h-12 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                </svg>
                                <p>Click or drag to upload media</p>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <label className="block text-sm font-medium mb-2">
                            {activeTool === 'hate-speech' ? 'Enter text to analyze' : 'Enter claim to verify'}
                          </label>
                          <textarea
                            value={trialInput}
                            onChange={(e) => setTrialInput(e.target.value)}
                            className="w-full p-4 rounded-xl border border-shayek-light-300 dark:border-shayek-dark-200 bg-white dark:bg-shayek-dark-50 focus:ring-2 focus:ring-shayek-primary transition-all"
                            placeholder={activeTool === 'hate-speech' ? 'e.g. "I love everyone"' : factCheckPlaceholder}
                            rows={4}
                            required
                          />
                        </div>
                      )}

                      {trialError && (
                        <p className="text-sm text-red-600 font-medium">{trialError}</p>
                      )}

                      <button
                        type="submit"
                        disabled={trialLoading || (activeTool === 'ai-detection' ? !trialFile : !trialInput.trim())}
                        className="w-full py-4 bg-shayek-primary text-white rounded-xl font-bold hover:bg-shayek-primary-600 disabled:opacity-50 transition-all shadow-shayek"
                      >
                        {trialLoading ? (
                          <span className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Analyzing...
                          </span>
                        ) : 'Start Free Analysis'}
                      </button>
                    </form>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div id="features" className="mb-16">
          <h3 className="text-3xl font-bold text-center text-shayek-light-900 dark:text-shayek-dark-900 mb-4">
            {t('landing.features.title')}
          </h3>
          {t('landing.features.description') && (
            <p className="text-center text-lg text-shayek-light-600 dark:text-shayek-dark-600 mb-12 max-w-3xl mx-auto">
              {t('landing.features.description')}
            </p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 hover:shadow-shayek-lg transition-all border border-shayek-light-300 dark:border-shayek-dark-200"
              >
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h4 className="text-xl font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-2">{feature.title}</h4>
                <p className="text-shayek-light-600 dark:text-shayek-dark-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* How It Works Section */}
        <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek-lg p-8 mb-16 border border-shayek-light-300 dark:border-shayek-dark-200">
          <h3 className="text-3xl font-bold text-center text-shayek-light-900 dark:text-shayek-dark-900 mb-12">
            {t('landing.howItWorks.title')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-circleBg border-2 border-shayek-primary dark:border-shayek-primary-600 shadow-shayek ring-2 ring-shayek-primary/20 dark:ring-shayek-primary/30">
                <span className="text-2xl font-bold text-circleNumber">1</span>
              </div>
              <h4 className="text-lg font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-2">
                {t('landing.howItWorks.step1.title')}
              </h4>
              <p className="text-shayek-light-600 dark:text-shayek-dark-600">{t('landing.howItWorks.step1.description')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-circleBg border-2 border-shayek-primary dark:border-shayek-primary-600 shadow-shayek ring-2 ring-shayek-primary/20 dark:ring-shayek-primary/30">
                <span className="text-2xl font-bold text-circleNumber">2</span>
              </div>
              <h4 className="text-lg font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-2">
                {t('landing.howItWorks.step2.title')}
              </h4>
              <p className="text-shayek-light-600 dark:text-shayek-dark-600">{t('landing.howItWorks.step2.description')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-circleBg border-2 border-shayek-primary dark:border-shayek-primary-600 shadow-shayek ring-2 ring-shayek-primary/20 dark:ring-shayek-primary/30">
                <span className="text-2xl font-bold text-circleNumber">3</span>
              </div>
              <h4 className="text-lg font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-2">
                {t('landing.howItWorks.step3.title')}
              </h4>
              <p className="text-shayek-light-600 dark:text-shayek-dark-600">{t('landing.howItWorks.step3.description')}</p>
            </div>
          </div>
        </div>

        {/* CTA Section - "Ready to Make a Difference" */}
        {!isAuthenticated && (
          <div className="bg-shayek-primary dark:bg-shayek-primary-600 rounded-lg shadow-shayek-red p-12 text-center text-white relative overflow-hidden">
            {/* Red pattern background */}
            <div className="absolute inset-0 bg-red-pattern-dark opacity-50"></div>
            <div className="relative z-10">
              <h3 className="text-3xl font-bold mb-4">{t('landing.cta.title')}</h3>
              <p className="text-xl mb-8 opacity-90">{t('landing.cta.subtitle')}</p>
              <Link
                to="/register"
                className="inline-block px-8 py-4 text-lg font-semibold bg-white text-shayek-primary rounded-lg hover:bg-shayek-light-50 transition-colors shadow-lg"
              >
                {t('landing.cta.button') || 'Get Started Now'}
              </Link>
            </div>
          </div>
        )}
        
        {/* Additional CTA for logged-in users */}
        {isAuthenticated && (
          <div className="bg-gradient-to-r from-shayek-light-50 to-shayek-light-100 dark:from-shayek-dark-50 dark:to-shayek-dark rounded-lg shadow-shayek-lg p-12 text-center border border-shayek-light-300 dark:border-shayek-dark-200">
            <h3 className="text-3xl font-bold mb-4 text-shayek-light-900 dark:text-shayek-dark-900">
              Continue Your Journey
            </h3>
            <p className="text-xl mb-8 text-shayek-light-600 dark:text-shayek-dark-600">
              Explore more features and make a real impact with our comprehensive moderation tools.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/dashboard"
                className="inline-block px-8 py-4 text-lg font-semibold bg-shayek-primary text-white rounded-lg hover:bg-shayek-primary-600 transition-colors shadow-lg"
              >
                Open Dashboard
              </Link>
              <Link
                to="/profile"
                className="inline-block px-8 py-4 text-lg font-semibold bg-white dark:bg-shayek-dark-100 text-shayek-primary rounded-lg hover:bg-shayek-light-50 dark:hover:bg-shayek-dark-50 transition-colors border-2 border-shayek-primary"
              >
                View Profile
              </Link>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-shayek-dark dark:bg-shayek-dark text-white mt-16 border-t border-shayek-primary/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h4 className="text-lg font-semibold mb-4 text-shayek-red-light">{t('landing.footer.about.title')}</h4>
              <p className="text-shayek-dark-600 dark:text-shayek-dark-600">{t('landing.footer.about.description')}</p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4 text-shayek-red-light">{t('landing.footer.links.title')}</h4>
              <ul className="space-y-2 text-shayek-dark-600 dark:text-shayek-dark-600">
                <li>
                  <Link to="/register" className="hover:text-shayek-red-light transition-colors">
                    {t('landing.footer.links.register', { defaultValue: 'Register' })}
                  </Link>
                </li>
                <li>
                  <Link to="/login" className="hover:text-shayek-red-light transition-colors">
                    {t('landing.footer.links.login', { defaultValue: 'Login' })}
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4 text-shayek-red-light">{t('landing.footer.contact.title')}</h4>
              <p className="text-shayek-dark-600 dark:text-shayek-dark-600">{t('landing.footer.contact.description')}</p>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-shayek-dark-200 dark:border-shayek-dark-200 text-center text-shayek-dark-600 dark:text-shayek-dark-600 space-y-2">
            <DisclaimerFooter className="!text-shayek-dark-500 dark:!text-shayek-dark-400" />
            <p>&copy; {new Date().getFullYear()} {t('common.appName')}. {t('landing.footer.rights')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;

